import { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { TensionLogo } from '../components/TensionLogo';
import { useSEO } from '../lib/seo';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { getAntiBotChallenge, solveAntiBotChallenge } from '../lib/db-storage';

const API_URL = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/contact`;

export default function BusinessInquiries() {
  useSEO({
    title: 'Investors & Business Inquiries | TENSION',
    description:
      'Contact TENSION for investor conversations, partnerships, press opportunities, and business development.',
    keywords: ['TENSION investors', 'business inquiries', 'partnership contact', 'press contact'],
    canonical: '/business-inquiries',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [searchParams] = useSearchParams();
  const initialInquiryType = searchParams.get('inquiryType') || 'investor';
  const [inquiryType, setInquiryType] = useState(initialInquiryType);
  const [companySize, setCompanySize] = useState('unknown');
  const [timeline, setTimeline] = useState('discovery');
  const [buyerRole, setBuyerRole] = useState('other');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const fieldLabelClass = 'text-xs font-semibold uppercase tracking-[0.12em] text-foreground/80';
  const selectClass =
    'h-11 w-full rounded-md border border-border bg-input px-3 text-sm text-foreground shadow-sm outline-none transition-colors focus-visible:border-violet-300/70 focus-visible:ring-2 focus-visible:ring-violet-400/55';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    try {
      const challenge = await getAntiBotChallenge('contact_form');
      const antiBotProof = await solveAntiBotChallenge(challenge);
      const subject = `[Business Inquiry] ${inquiryType} - ${company || name}`;
      const formattedMessage = [
        `Inquiry Type: ${inquiryType}`,
        `Company: ${company || 'N/A'}`,
        `Buyer Role: ${buyerRole}`,
        `Company Size: ${companySize}`,
        `Timeline: ${timeline}`,
        '',
        message.trim(),
      ].join('\n');

      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject,
          message: formattedMessage,
          antiBotToken: antiBotProof.token,
          antiBotNonce: antiBotProof.nonce,
        }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || 'Unable to submit inquiry.');
      }

      toast.success('Inquiry submitted successfully. We will follow up shortly.');
      setName('');
      setEmail('');
      setCompany('');
      setInquiryType('investor');
      setCompanySize('unknown');
      setTimeline('discovery');
      setBuyerRole('other');
      setMessage('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to submit inquiry.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen py-10 px-4">
      <div className="mx-auto max-w-3xl space-y-6">
        <StockHeroStrip stockKey="supportCalm" alt="Professional collaboration — illustrative stock" />
        <header className="rounded-2xl border border-violet-300/30 bg-[color-mix(in_srgb,var(--card)_74%,rgba(17,24,39,0.62)_26%)] p-6 shadow-[var(--ds-card-edge-glow),var(--ds-shadow-soft)] backdrop-blur-xl">
          <div className="flex items-center justify-start">
            <TensionLogo tone="dark" height={50} priority className="h-auto max-w-[230px] sm:max-w-[280px]" />
          </div>
          <h1 className="mt-4 text-3xl font-bold text-foreground">Investors & Business Inquiries</h1>
          <p className="mt-2 text-foreground/78">
            Use this form for investor interest, strategic partnerships, press collaboration, and enterprise opportunities.
          </p>
        </header>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="biz-name" className={fieldLabelClass}>Name</Label>
                <Input id="biz-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="biz-email" className={fieldLabelClass}>Email</Label>
                <Input id="biz-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="biz-company" className={fieldLabelClass}>Company / Firm</Label>
                <Input id="biz-company" value={company} onChange={(e) => setCompany(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="biz-type" className={fieldLabelClass}>Inquiry type</Label>
                <select
                  id="biz-type"
                  value={inquiryType}
                  onChange={(e) => setInquiryType(e.target.value)}
                  className={selectClass}
                >
                  <option value="investor">Investor</option>
                  <option value="business-partnership">Business partnership</option>
                  <option value="press">Press / media</option>
                  <option value="enterprise">Enterprise / B2B</option>
                </select>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="biz-role" className={fieldLabelClass}>Buyer role</Label>
                <select
                  id="biz-role"
                  value={buyerRole}
                  onChange={(e) => setBuyerRole(e.target.value)}
                  className={selectClass}
                >
                  <option value="hr">HR / People Ops</option>
                  <option value="benefits">Benefits / EAP</option>
                  <option value="clinical">Clinical / Coaching</option>
                  <option value="investor">Investor</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="biz-size" className={fieldLabelClass}>Company size</Label>
                <select
                  id="biz-size"
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                  className={selectClass}
                >
                  <option value="unknown">Not specified</option>
                  <option value="1-100">1-100</option>
                  <option value="101-500">101-500</option>
                  <option value="501-2000">501-2,000</option>
                  <option value="2001-10000">2,001-10,000</option>
                  <option value="10000+">10,000+</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="biz-timeline" className={fieldLabelClass}>Decision timeline</Label>
                <select
                  id="biz-timeline"
                  value={timeline}
                  onChange={(e) => setTimeline(e.target.value)}
                  className={selectClass}
                >
                  <option value="discovery">Researching / discovery</option>
                  <option value="0-3-months">0-3 months</option>
                  <option value="3-6-months">3-6 months</option>
                  <option value="6-12-months">6-12 months</option>
                  <option value="12+-months">12+ months</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="biz-message" className={fieldLabelClass}>Message</Label>
              <Textarea
                id="biz-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={7}
                placeholder="What outcomes, use-cases, and pilot scope are you interested in?"
                required
              />
            </div>

            <p className="text-sm text-foreground/75">
              Automated anti-bot verification runs in the background when you submit.
            </p>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
            >
              {loading ? 'Sending...' : 'Send inquiry'}
            </Button>
          </form>
        </Card>

        <p className="text-sm text-foreground/75">
          Need background context first? <Link to="/press" className="text-violet-300 underline">Press page</Link>
        </p>
      </div>
    </div>
    </WarmMarketingBackdrop>
  );
}
